import { Lender } from '@1delta/lender-registry'
import { shortDate } from '../terms/format'
import type { MaturityTerms } from '../terms/types'
import { VAULT_VENUE_PREFIX } from './uid'

/**
 * Display vocabulary for the earn surface — **owned by the server**.
 *
 * A client must not carry its own copy of any of this. That is not a style
 * preference: every label map shipped in a frontend is a copy of server state
 * that rots silently. When a new exit mode or vault provider is integrated, a
 * client-side `switch` renders it as blank, as "Unknown", or worse, falls into
 * a `default` branch that quietly misdescribes it — and nobody notices until a
 * user acts on the wrong description.
 *
 * Putting the labels here means adding a mode is one change on one side, and
 * every consumer picks it up on the next request.
 *
 * The rule for consumers is simply: **render `label ?? key`**. An unrecognised
 * value then renders as itself, which is honest, rather than as a guess.
 */

/** `lending` / `vault` — the two halves of the merged listing. */
const VENUE_KIND_LABELS: Record<string, string> = {
  lending: 'Lending markets',
  vault: 'Vaults',
}

const VENUE_KIND_DESCRIPTIONS: Record<string, string> = {
  lending:
    'Supply into a lending market. Same-block exit, bounded by liquidity.',
  vault: 'Deposit into a yield vault. Exit terms vary per vault.',
}

/**
 * Short labels for `SupplyExitMode`. `terms/format.ts` already carries a
 * sentence-form `exitPhrase` for prose; these are the badge-sized versions and
 * the two are kept deliberately separate — a table cell and a paragraph want
 * different lengths.
 */
const EXIT_MODE_LABELS: Record<string, string> = {
  instant: 'Instant',
  'instant-capped': 'Instant (capped)',
  'instant-or-queued': 'Instant or queue',
  'fee-or-queued': 'Fee or queue',
  'fixed-cooldown': 'Cooldown',
  queued: 'Queued',
  'request-based': 'Request',
  'market-sale': 'Market sale',
  'at-maturity': 'At maturity',
  'off-chain': 'Off-chain',
  'dex-only': 'DEX only',
}

const EXIT_MODE_DESCRIPTIONS: Record<string, string> = {
  instant: 'Withdraw any time.',
  'instant-capped': 'Withdraw up to the available liquidity.',
  'instant-or-queued': 'Withdraw instantly, or queue for the rest.',
  'fee-or-queued': 'Instant exit for a fee, or queue for free.',
  'fixed-cooldown': 'Request, wait out a cooldown, then claim.',
  queued: 'Withdrawals are queued.',
  'request-based': 'Withdrawals need a request and a later claim.',
  'market-sale': 'Exit by selling the position on a market.',
  'at-maturity': 'Locked until maturity.',
  'off-chain': 'Exit is settled off-chain.',
  'dex-only': 'Exit only by selling on a DEX.',
}

const ACTION_LABELS: Record<string, string> = {
  deposit: 'Deposit',
  withdraw: 'Withdraw',
  'request-withdraw': 'Request withdrawal',
  claim: 'Claim',
  cancel: 'Cancel request',
}

/**
 * Why a row cannot be entered. Rendered on the disabled CTA, so it must read as
 * an explanation to a user, not as an enum to a developer.
 */
const GATING_LABELS: Record<string, string> = {
  'allowlist-contract': 'Permissioned',
  kyc: 'KYC required',
  'cap-full': 'Cap full',
  paused: 'Paused',
  frozen: 'Frozen',
  'collateral-only': 'No yield',
  matured: 'Matured',
}

const RATE_KIND_LABELS: Record<string, string> = {
  'variable-curve': 'Variable (utilization)',
  'variable-managed': 'Variable (managed)',
  'user-set': 'Borrower-set',
  'fixed-term': 'Fixed term',
  'fixed-open': 'Fixed',
  'zero-interest': 'Interest-free',
  prepaid: 'Prepaid',
  'nav-accrual': 'NAV accrual',
  none: 'No rate',
}

const RATE_SOURCE_LABELS: Record<string, string> = {
  chain: 'On-chain',
  api: 'Protocol API',
  oracle: 'Oracle',
  realized: 'Realized',
}

/**
 * Venue prefixes that are NOT `Lender` members, because the per-market key is
 * synthesized at runtime and only the suffixed form was ever enumerated.
 *
 *  - `AAVE_V4` — spokes are minted by `aaveV4SpokeLenderKey()`; the hub name is
 *    deliberately not an enum member.
 *  - `COMPOUND_V3` — the enum carries one member PER COMET
 *    (`COMPOUND_V3_USDC`, `…_WETH`, 11 of them) and no family key.
 *  - `SWAYLEND` — enumerated only as `SWAYLEND_USDC`.
 *
 * Without these the family match below stops at the market key and the brand
 * reads "Compound V3 Usdc" — a per-market brand, which is the thing brands
 * exist to collapse.
 */
const SYNTHETIC_FAMILIES = ['AAVE_V4', 'COMPOUND_V3', 'SWAYLEND'] as const

/**
 * Every family key, SHORTEST FIRST.
 *
 * Order is the whole algorithm. `venueBrandKey` takes the FIRST match, so the
 * shortest enum key that is a prefix wins and the sub-market members collapse
 * into their family: `VENUS_ETHENA` → `VENUS`, `AVALON_SOLVBTC` → `AVALON`,
 * `AAVE_V3_PRIME` → `AAVE_V3`, `COMPOUND_V3_WETH` → `COMPOUND_V3`.
 *
 * Longest-first — what `lenderBrandKey` in `lender-registry` does, correctly,
 * for ICONS — would keep every one of those as its own brand and scatter the
 * facet across 100+ near-duplicate entries. The two getters want opposite
 * ends of the same list, which is why this one is not simply that one.
 */
const FAMILY_KEYS: readonly string[] = [
  ...new Set<string>([
    ...(Object.values(Lender) as string[]),
    ...SYNTHETIC_FAMILIES,
  ]),
].sort((a, b) => a.length - b.length || a.localeCompare(b))

/**
 * Brands whose spelling title-casing gets wrong.
 *
 * Deliberately SMALL. The default is derived (`MORPHO_BLUE` → "Morpho Blue"),
 * so this table holds only the genuine exceptions — internal capitals, initial
 * lowercase, all-caps names and the two brands whose key carries a suffix the
 * brand itself does not use. Everything absent is title-cased, which is right
 * for ~170 of the 186 lenders.
 *
 * The point of deriving rather than listing: a newly integrated lender is
 * branded the moment its enum member exists. The old hand table covered 29 of
 * 186 keys, so 157 lenders rendered as raw `SCREAMING_SNAKE` keys.
 */
const BRAND_OVERRIDES: Record<string, string> = {
  LISTA_DAO: 'Lista',
  FLUX_FINANCE: 'Flux',
  TERMMAX: 'TermMax',
  LLAMALEND: 'LlamaLend',
  USDD: 'USDD',
  USDAF: 'USDaf',
  DTRINITY: 'dTRINITY',
  DFORCE: 'dForce',
  OVIX: '0VIX',
  WE_PIGGY: 'WePiggy',
  ZEROLEND: 'ZeroLend',
  HYPERLEND: 'HyperLend',
  HYPURRFI: 'HypurrFi',
  HYPERYIELD: 'HyperYield',
  LAYERBANK_V3: 'LayerBank V3',
  TAKOTAKO: 'TakoTako',
  SWAYLEND: 'Swaylend',
  BENQI: 'BENQI',
  YLDR: 'YLDR',
  YEI: 'YEI',
  RMM: 'RMM',
  PAC: 'PAC',
}

/**
 * `MORPHO_BLUE` → `Morpho Blue`, `COMPOUND_V3` → `Compound V3`.
 *
 * A segment of one letter followed by digits survives intact (`V3`, `U235`),
 * which is why the version suffixes need no special case.
 */
function titleCaseKey(key: string): string {
  return key
    .split(/[_-]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ')
}

/** Vault providers → their display brand, when the row carries no curator. */
const VAULT_PROVIDER_BRANDS: Record<string, string> = {
  fluid: 'Fluid',
  gearbox: 'Gearbox',
  morpho: 'Morpho',
  lista: 'Lista',
  silo: 'Silo',
  'euler-earn': 'Euler',
  termmax: 'TermMax',
  lst: 'Liquid staking',
  savings: 'Savings',
  lagoon: 'Lagoon',
  'aave-earn': 'Aave',
  upshift: 'Upshift',
  yearn: 'Yearn',
  hypercore: 'HyperLiquid',
  gmx: 'GMX',
  pendle: 'Pendle',
  spectra: 'Spectra',
}

/**
 * A key segment that identifies an instance rather than a protocol: a hex blob,
 * an address, or a bare number. Everything from the first such segment onward
 * is instance detail, not brand.
 */
function isInstanceSegment(seg: string): boolean {
  if (/^\d+$/.test(seg)) return true
  if (/^0X[0-9A-F]+$/i.test(seg)) return true
  return seg.length >= 8 && /^[0-9A-F]+$/i.test(seg)
}

/**
 * The STABLE family key behind a venue — `MORPHO_BLUE_1E9D…` → `MORPHO_BLUE`,
 * `FLUID_1_11` → `FLUID`, `vault.savings` → `vault.savings`.
 *
 * This is the identifier a client can filter and cache on. The venue key
 * itself cannot serve that purpose on the lending half: it is minted per
 * market, so `?venue=` needs the exact 32-byte Morpho id and a "Morpho"
 * filter is unexpressible.
 *
 * Vault venues are already family-shaped (`vault.<provider>`) and pass
 * through unchanged.
 */
export function venueBrandKey(venue: string): string {
  if (venue.startsWith(VAULT_VENUE_PREFIX)) return venue

  const key = venue.toUpperCase()
  // Shortest match wins — see FAMILY_KEYS. The `_` boundary is what keeps
  // TERMMAX from resolving to TERM_FINANCE and vice versa.
  for (const family of FAMILY_KEYS) {
    if (key === family || key.startsWith(family + '_')) return family
  }

  // Not a known lender at all. Cut at the first instance-looking segment so a
  // key we have never seen still collapses: SOME_LENDER_0xAbC… → SOME_LENDER.
  const segments = key.split('_')
  const cut = segments.findIndex(isInstanceSegment)
  return (cut > 0 ? segments.slice(0, cut) : segments).join('_')
}

/**
 * Collapse a venue key to its display brand.
 *
 * `MORPHO_BLUE_1E9D…` → `Morpho Blue`; `FLUID_1_11` → `Fluid`;
 * `SKY_1_ETH_A` → `Sky`; `vault.savings` → `Savings`.
 *
 * Derived from the `Lender` enum, not from a hand-maintained table, so every
 * integrated lender is named and a new one is named the day its enum member
 * lands. An unknown key still renders as its own collapsed family — terse but
 * true, never a guess.
 */
export function venueBrand(venue: string): string {
  if (venue.startsWith(VAULT_VENUE_PREFIX)) {
    const provider = venue.slice(VAULT_VENUE_PREFIX.length)
    return VAULT_PROVIDER_BRANDS[provider] ?? titleCaseKey(provider)
  }
  const family = venueBrandKey(venue)
  return BRAND_OVERRIDES[family] ?? titleCaseKey(family)
}

/** Every dimension the earn surface labels, in one lookup. */
export const EARN_LABELS = {
  venueKind: VENUE_KIND_LABELS,
  exitMode: EXIT_MODE_LABELS,
  action: ACTION_LABELS,
  gating: GATING_LABELS,
  rateKind: RATE_KIND_LABELS,
  rateSource: RATE_SOURCE_LABELS,
} as const

export const EARN_DESCRIPTIONS = {
  venueKind: VENUE_KIND_DESCRIPTIONS,
  exitMode: EXIT_MODE_DESCRIPTIONS,
} as const

export type EarnLabelDimension = keyof typeof EARN_LABELS

/**
 * Look up a label, falling back to the raw key.
 *
 * The fallback is the contract: an unlabelled value renders as itself, so a
 * newly added mode is legible (if terse) everywhere immediately, and adding its
 * label later is a pure improvement rather than a bug fix.
 */
export function earnLabel(dimension: EarnLabelDimension, key: string): string {
  return EARN_LABELS[dimension][key] ?? key
}

export function earnDescription(
  dimension: keyof typeof EARN_DESCRIPTIONS,
  key: string,
): string | undefined {
  return EARN_DESCRIPTIONS[dimension][key]
}

// ---------------------------------------------------------------------------
// Market naming
// ---------------------------------------------------------------------------

export interface EarnMarketLabelInput {
  /** The asset being supplied, e.g. `USDC`. */
  assetSymbol?: string
  /**
   * Symbols of the collateral(s) that can be posted against this market.
   *
   * Length is the whole signal — see {@link earnMarketLabel}. Pass the real
   * list; do not pre-truncate it, or a shared pool with 30 collaterals becomes
   * indistinguishable from an isolated pair with 1.
   */
  collateralSymbols?: string[]
  /**
   * The LENDER's own name for this market, as `lenderInfo.name` —
   * `Morpho cbBTC-USDC 86`, `TermMax RLUSD / USPC — 2026-10-25`,
   * `Aave V4 Etherfi`, or just `Aave V3` for a shared pool.
   *
   * Preferred over the derived pair because it carries what a derived pair
   * cannot: the **LLTV** that separates three otherwise identical
   * `USDC · vs WBTC` Morpho markets, the **maturity** on a fixed-term market,
   * and the **instance** on a multi-spoke deployment.
   */
  lenderMarketName?: string
  /**
   * The row's venue key, used to strip the part of the lender's name that the
   * brand already states.
   *
   * The VENUE and not the brand string, because a brand override can be
   * SHORTER than the name it has to cancel: `FLUX_FINANCE` displays as "Flux",
   * so stripping by the brand alone leaves `Flux Finance` → "Finance" and the
   * row reads "USDT · Finance". Both the display brand and the family key
   * contribute words.
   */
  venue?: string
  /**
   * The row's maturity, when it has one.
   *
   * Appended to whatever label the rules below produce, because a fixed-term
   * product's date is part of its identity — two PTs on one underlying are
   * otherwise indistinguishable. See {@link withMaturityLabel}.
   */
  maturity?: MaturityTerms
  /**
   * The name the PUBLIC-DATA FETCHER produced for this row.
   *
   * Quality varies by lender and that is the whole point of treating it as a
   * candidate rather than a fallback: most emit a per-LEG label
   * (`'Loan ' + symbol`, `'Collateral ' + symbol` — Compound V3, Fluid, Lista,
   * Term, TermMax, Midnight all do), which says nothing about WHICH market it
   * is. Euler's parser instead sets the eVault's own on-chain `vaultName`, so
   * `Prime USDC` was sitting on the row while the label fell through to plain
   * `USDC` and 530 Euler markets rendered as their asset.
   *
   * Leg names are rejected; anything else is used.
   */
  fetcherName?: string
  /**
   * Which slice of a tranched vault this is.
   *
   * Strata names its tranches `srUSDe` / `jrUSDe` — two characters carrying
   * the entire difference between a senior claim and FIRST-LOSS capital that
   * pays roughly double and can print a negative trailing APR. Read as jargon
   * or skimmed past, those two rows look like the same product at two rates.
   *
   * Taken from `risk.counterparty` (`tranched-senior` / `tranched-junior`),
   * which the registry already sets per vault — so this states in words what
   * the data model already knows, rather than parsing a symbol prefix.
   */
  tranche?: 'senior' | 'junior'
  /** Used only when there is no asset symbol at all. */
  fallbackName?: string
}

/**
 * Which tranche a row is — from `solvency`, and ONLY from `solvency`.
 *
 * A senior claim and FIRST-LOSS capital that pays roughly double and can print
 * a negative trailing APR are different products, and Strata separates them
 * with two characters: `srUSDe` vs `jrUSDe`. The savings registry already
 * records which is which (`tranched-senior` / `tranched-junior`) and the SDK
 * publishes it, so the answer exists — it just does not survive the pipeline:
 * there is no `savings_solvency` column, so the ingest drops it and `/vaults`
 * never serves it. Adding that column is the fix.
 *
 * **Do not read the tranche off the symbol prefix.** It was tried and measured
 * against all 396 live chain-1 vault rows: `^(sr|jr)[A-Z]` is clean but tags
 * only 8 of Strata's 12 (Midas-backed tranches spell it `srmHYPER`,
 * `jrmM1-USD`), and widening it to `^(sr|jr)[a-zA-Z]` reaches all 12 at the
 * cost of claiming Reserve's `sreUSD` — a plain savings token — is a senior
 * tranche. No prefix separates them, and labelling 8 of 12 is worse than
 * labelling none: it reads as "the other four are not tranches".
 */
export function trancheFromCounterparty(
  counterparty: string | undefined,
): 'senior' | 'junior' | undefined {
  if (counterparty === 'tranched-senior') return 'senior'
  if (counterparty === 'tranched-junior') return 'junior'
  return undefined
}

/**
 * `Loan USDC` / `Collateral cbBTC` — a LEG of a market, not the market.
 *
 * Both legs of one market carry these, so they can never identify it: a chain
 * with 300 Morpho markets renders 300 rows reading `Loan USDC`. That is the
 * original defect this module was written for.
 */
function isLegName(name: string, asset: string): boolean {
  const m = name.trim().match(/^(?:loan|collateral)\s+(.+)$/i)
  return !!m && m[1].trim().toLowerCase() === asset.toLowerCase()
}

/**
 * Drop the leading words of a market name that the brand already states.
 *
 * `Morpho cbBTC-USDC 86` + venue `MORPHO_BLUE_…` → `cbBTC-USDC 86`;
 * `Aave V3 Prime` + `AAVE_V3_PRIME` → `Prime`; `Aave V3` + `AAVE_V3` → ``
 * (empty: the name says nothing the brand did not, i.e. a shared pool).
 *
 * Word-set rather than string-prefix, because the two disagree constantly:
 * the brand is `Morpho Blue` while the name says `Morpho`, the brand is
 * `Compound V3` while the name says `Compound`, and `dTRINITY` vs `dTrinity`
 * differs only in case. A prefix test fails all three.
 */
function stripBrandWords(name: string, venue?: string): string {
  // Deliberately the EMPTY-permitting form: an empty remainder is the signal
  // `earnMarketLabel` reads as "this name said only what the brand says".
  return dropLeadingBrandWords(
    name,
    venue ? `${venueBrand(venue)} ${venueBrandKey(venue)}` : '',
  )
}

function dropLeadingBrandWords(name: string, brand: string): string {
  const brandWords = new Set(
    brand
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  )
  const words = name.trim().split(/\s+/)
  let i = 0
  while (i < words.length && brandWords.has(words[i].toLowerCase())) i++
  return words.slice(i).join(' ').trim()
}

/**
 * The same rule against an EXPLICIT brand, for the rows whose brand does not
 * come from a venue key.
 *
 * A vault's brand is its CURATOR — "Frankencoin", "9Summits", "Lido" — which
 * `venueBrand('vault.savings')` cannot know, so the vault path needs this
 * form. It matters because the brand renders in the subtitle beside the name:
 * without it "Frankencoin Savings Module" sits under "Frankencoin · Vaults"
 * and "Lido wstETH" under "Lido · Vaults", each saying the brand twice.
 *
 * Returns the input unchanged when stripping would leave nothing — a vault
 * genuinely named after its brand and nothing else ("Ethena") must keep that
 * name rather than render blank.
 */
export function stripLeadingBrand(name: string, brand?: string): string {
  if (!brand) return name.trim()
  return dropLeadingBrandWords(name, brand) || name.trim()
}

/** Case-insensitive whole-token test: `ETH` is NOT inside `wstETH-USDC`. */
function namesToken(text: string, token: string): boolean {
  const wanted = token.toLowerCase()
  // The UNWRAPPED form counts as a match. Sky's ilk is `ETH-A` while the token
  // it takes is WETH, so `DAI / ETH-A` does state this row's asset — without
  // this the label reads `WETH · DAI / ETH-A`, repeating a fact the name and
  // the asset column both already carry. Only a leading `W` is stripped, and
  // only when something substantial remains, so a short ticker is left alone.
  const unwrapped =
    wanted.startsWith('w') && wanted.length >= 4 ? wanted.slice(1) : undefined
  return text.split(/[^a-zA-Z0-9]+/).some((t) => {
    const seg = t.toLowerCase()
    return seg === wanted || (unwrapped !== undefined && seg === unwrapped)
  })
}

/**
 * A market label that actually distinguishes one market from another.
 *
 * The problem this solves: an isolated market is a **(collateral, loan) pair**,
 * but the fetcher emits it as two rows each naming only its own leg — so a
 * chain with 300 Morpho Blue markets renders 300 rows all reading
 * "Loan USDC". The identity lives in the relationship between the legs, and
 * neither leg's name can express it.
 *
 * Three sources, in descending order of what they can express:
 *
 * **1. The lender's own market name**, once the brand prefix is stripped. This
 * wins where it exists because it carries what nothing derived can — the LLTV
 * (`cbBTC-USDC 86`), the maturity (`RLUSD / USPC — 2026-10-25`), the spoke
 * (`Etherfi`). Three Morpho markets on the very same pair differ ONLY by LLTV,
 * so a pair-derived label leaves them identical and reproduces the same
 * complaint one step later.
 *
 * The asset is prefixed only when the name does not already state it:
 *
 * ```
 * 'Morpho cbBTC-USDC 86' + USDC  → 'cbBTC-USDC 86'   name already says USDC
 * 'Aave V4 Etherfi'      + weETH → 'weETH · Etherfi'  name does not
 * 'Compound USDC'        + USDC  → (nothing to add — falls through)
 * 'Aave V3'              + WETH  → (nothing to add — falls through)
 * ```
 *
 * **2. The derived pair**, when the lender publishes no name — 12 Fluid and 4
 * Silo V3 rows on chain 1 today. **The collateral is named exactly when the
 * market is ISOLATED**, i.e. exactly one collateral pairs with it:
 *
 * ```
 * 1 collateral   → 'USDC · vs wstETH'   the collateral IS the identity
 * many           → 'USDC'               a shared pool; naming 1 of 30 misleads
 * none           → 'USDC'               collateral-only or unpaired
 * ```
 *
 * Derived, never configured. No table says "Morpho is isolated, Aave is not" —
 * the pair count says it, so a newly integrated isolated lender labels itself
 * correctly with no code change here.
 *
 * **3. The asset alone**, which is the right answer for a shared pool: the
 * brand renders beside it, and picking one of thirty collaterals would assert
 * something false.
 */
export function earnMarketLabel(input: EarnMarketLabelInput): string {
  const base = withTrancheLabel(baseMarketLabel(input), input.tranche) ?? ''
  return withMaturityLabel(base, input.maturity) ?? ''
}

/**
 * Spell out a tranche the name only encodes.
 *
 * Skipped when the name already says it in words, so a vault called
 * "… Senior Tranche" is not stamped twice. The two-letter `sr`/`jr` prefix
 * does NOT count as saying it — that is the whole reason this exists.
 */
export function withTrancheLabel(
  name: string | undefined,
  tranche?: 'senior' | 'junior',
): string | undefined {
  if (!name || !tranche) return name
  const word = tranche === 'senior' ? 'Senior' : 'Junior'
  if (name.toLowerCase().includes(word.toLowerCase())) return name
  return `${name} · ${word}`
}

function baseMarketLabel(input: EarnMarketLabelInput): string {
  const asset = input.assetSymbol?.trim()
  if (!asset) return input.fallbackName ?? ''

  const detail = stripBrandWords(
    input.lenderMarketName?.trim() ?? '',
    input.venue,
  )
  // An empty remainder means the name said only what the brand says (a shared
  // pool); a remainder equal to the asset means it added only what the asset
  // column already shows. Neither is market identity.
  if (detail && detail.toLowerCase() !== asset.toLowerCase()) {
    return namesToken(detail, asset) ? detail : `${asset} · ${detail}`
  }

  // The fetcher's own name, where it names the MARKET rather than a leg. This
  // is the lowest level that knows the answer — Euler reads it off the eVault —
  // so it outranks anything derived below.
  const own = stripBrandWords(input.fetcherName?.trim() ?? '', input.venue)
  if (
    own &&
    !isLegName(own, asset) &&
    own.toLowerCase() !== asset.toLowerCase()
  ) {
    return namesToken(own, asset) ? own : `${asset} · ${own}`
  }

  // Self excluded HERE rather than by the caller: on the collateral row of an
  // isolated market the only collateral is itself, and "cbBTC · vs cbBTC" is
  // nonsense.
  const collaterals = (input.collateralSymbols ?? [])
    .map((c) => c?.trim())
    .filter((c): c is string => !!c && c !== asset)

  // Deduplicate before counting: the same collateral can pair through several
  // e-mode categories, and three rows for one asset is still ONE collateral.
  const distinct = [...new Set(collaterals)]

  // Exactly one ⇒ isolated. Anything else names the asset alone: for a shared
  // pool, picking one of many collaterals would assert something false about
  // what the market is.
  if (distinct.length === 1) return `${asset} · vs ${distinct[0]}`
  return asset
}

/**
 * Append a fixed-maturity row's date to its name, when the name omits it.
 *
 * **A maturity is part of a fixed-term product's identity, not metadata about
 * it.** Pendle publishes names like `PT wstETH (stETH) Ethereum` with no date,
 * and on chain 1 alone that produces three collisions — one pair maturing
 * 2026-08-27 and 2027-12-30, sixteen months apart, rendered as the same row.
 * Two rows with one name are not a display nit here: they are different bonds
 * at different prices, and a user picks the wrong one.
 *
 * Idempotent by inspection rather than by flag: a venue whose name ALREADY
 * carries the date (TermMax spells `… — 2026-10-25`, and Pendle's own UI shows
 * one) must not get it twice. The year is the cheap discriminator — a name
 * bearing the maturity year is assumed to state the maturity.
 */
export function withMaturityLabel(
  name: string | undefined,
  maturity: MaturityTerms | undefined,
): string | undefined {
  const secs = maturity?.maturity
  if (!name || secs == null || !Number.isFinite(secs)) return name
  const year = String(new Date(secs * 1000).getUTCFullYear())
  if (name.includes(year)) return name
  return `${name} · ${shortDate(secs)}`
}

/**
 * The row's provenance, as ONE ready-to-render string: who runs it · what it
 * runs on · kind. `Gauntlet · Morpho · Vaults`, `Aave V3 · Lending markets`.
 *
 * Published so that no client composes it. Three surfaces were assembling it
 * from `curator` + `brand`/`protocol` + `venueKind` and all three disagreed —
 * and it broke silently when `protocol.name` became version-free for grouping,
 * because the frontend was still rendering that field and Aave V2 and Aave V3
 * rows started reading identically. Nothing client-side could catch it: the
 * rule lives here.
 *
 * The SECOND segment is not always the same field, and that is the whole
 * subtlety:
 *
 *  - **With a curator**, it is the PROTOCOL. `brand` is documented as "curator
 *    where one exists, else the protocol", and the data bears it out — on
 *    chain 1 every one of the 54 curated rows has `brand === curator.name`. So
 *    `curator · brand` was always the same word twice ("Tulipa Capital ·
 *    Tulipa Capital · Vaults"). What the curator's name cannot tell you is
 *    which stack the deposit lands in, which is exactly the protocol.
 *  - **Without one**, it is the BRAND, because that keeps the generation:
 *    `AAVE_V3` arrives as protocol `Aave`, brand `Aave V3`, and an Aave V2 row
 *    reading identically to an Aave V3 row is worse than a less canonical name.
 *
 * The dedupe survives either way — it still collapses `Strata · Strata`, and a
 * future row whose curator and protocol coincide degrades to one segment
 * rather than to a stutter.
 *
 * The ASSET is deliberately absent: every surface shows it separately (a
 * column in the table, the amount field in the panel), so folding it in here
 * would duplicate by construction rather than by accident.
 */
export function earnRowSubtitle(row: {
  brand?: string
  protocol?: { name: string }
  curator?: { name?: string }
  venueKind: string
}): string {
  const curator = row.curator?.name?.trim()
  const stack = curator
    ? (row.protocol?.name ?? row.brand)
    : (row.brand ?? row.protocol?.name)

  const out: string[] = []
  const seen = new Set<string>()
  for (const part of [curator, stack, earnLabel('venueKind', row.venueKind)]) {
    const value = part?.trim()
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out.join(' · ')
}

/** Stamp `subtitle` onto every row. */
export function stampEarnSubtitles(
  rows: Array<Parameters<typeof earnRowSubtitle>[0] & { subtitle?: string }>,
): void {
  for (const row of rows) row.subtitle = earnRowSubtitle(row)
}

// ---------------------------------------------------------------------------
// Disambiguation
// ---------------------------------------------------------------------------

/**
 * What a user can actually tell one row from another by.
 *
 * Not the uid: every row has a unique one by construction, so keying on it
 * would declare a listing distinct while every visible field matched.
 */
function renderedIdentity(m: {
  chainId: string
  brand?: string
  venue: string
  name?: string
  asset: { symbol: string }
}): string {
  return [m.chainId, m.brand ?? m.venue, m.name ?? '', m.asset.symbol].join('|')
}

/**
 * `0x1234abcd…` → `0x1234`. Short enough to read, and — with `chars` — long
 * enough to actually separate the rows it is decorating.
 *
 * **Only hex.** It used to accept anything ≥ 6 characters, so a venue segment
 * like `vault.morpho` came back as the meaningless `0xvaul` — non-undefined
 * and identical for every row in the group, which burned a discriminator slot
 * without separating anything.
 */
function abbreviate(
  address: string | undefined,
  chars = 4,
): string | undefined {
  const a = address?.trim()
  if (!a) return undefined
  // Case preserved in the OUTPUT (the suffix renders as the source wrote it)
  // and ignored in the CHECK.
  const hex = /^0x/i.test(a) ? a.slice(2) : a
  if (!/^[0-9a-fA-F]+$/.test(hex) || hex.length < 4) return undefined
  return `0x${hex.slice(0, chars)}`
}

/**
 * Give colliding rows a suffix, and ONLY colliding rows.
 *
 * Everything else in this module labels a row from its own fields, which
 * cannot fix the last case: two markets on the same pair, at the same LLTV,
 * whose only difference is the oracle. `cbBTC-USDC 86` appears three times on
 * chain 1 ($532M / $5M / $54k) and one of them prices cbBTC off `BTC / USD` —
 * which ignores a cbBTC depeg entirely.
 *
 * Applied as a whole-listing pass rather than per row because "is this
 * ambiguous?" is not answerable from one row, and because suffixing every
 * market would be noise: 450 of 592 Morpho rows would gain a redundant oracle
 * that restates their own pair.
 *
 * Discriminators, in order — the first that actually SEPARATES the group wins,
 * so a suffix is never added that leaves the rows just as ambiguous:
 *
 *  1. what the oracle prices (`BTC / USD`) — a risk difference, and readable
 *  2. the oracle's address, abbreviated (`0xc7be`)
 *  3. the market id from the venue key, abbreviated (`0x4fe7`)
 *
 * Mutates `name` in place and returns the number of rows changed.
 */
export function disambiguateEarnNames(
  rows: Array<{
    chainId: string
    brand?: string
    venue: string
    name?: string
    ref?: string
    asset: { symbol: string }
    refs?: { oracle?: string; oracleDescription?: string }
  }>,
): number {
  const groups = new Map<string, typeof rows>()
  for (const m of rows) {
    const k = renderedIdentity(m)
    const g = groups.get(k)
    if (g) g.push(m)
    else groups.set(k, [m])
  }

  const candidates: Array<(m: (typeof rows)[number]) => string | undefined> = [
    (m) => m.refs?.oracleDescription?.trim() || undefined,
    (m) => abbreviate(m.refs?.oracle),
    (m) => abbreviate(m.venue.split('_').pop()),
    // The uid's third segment — the cToken on Compound V2, where BOTH the
    // legacy `cWBTC` and `cWBTC2` markets publish the venue `COMPOUND_V2` and
    // fall back to the asset name. It is the market's on-chain identity, so it
    // always separates; last because it is the least readable.
    (m) => abbreviate(m.ref),
  ]

  let changed = 0
  for (const group of groups.values()) {
    if (group.length < 2) continue
    const marks = separatingMarks(group, candidates)
    if (!marks) continue
    group.forEach((m, i) => {
      m.name = m.name ? `${m.name} · ${marks[i]}` : marks[i]
      changed++
    })
  }
  return changed
}

/**
 * The first discriminator that actually SEPARATES the group, widening the
 * address before giving up.
 *
 * The widening is not a nicety. MetaMorpho vaults are deployed at vanity
 * addresses that all begin `0xbeef`, so four `Steakhouse Financial · USDC`
 * rows on chain 1 produced FOUR IDENTICAL `0xbeef` marks — every candidate
 * failed, the group kept its collision, and the audit that exists to catch
 * exactly this reported it. Six characters is a readable default and a wrong
 * invariant; "as many as it takes" is the invariant, and the common case is
 * still six.
 *
 * Returns `undefined` when nothing separates the rows — two rows sharing a
 * chain, a brand, a name, an asset AND an address are the same row twice,
 * which is an ingest bug a suffix would only hide.
 */
function separatingMarks<
  T extends {
    ref?: string
    venue: string
    refs?: { oracle?: string; oracleDescription?: string }
  },
>(
  group: T[],
  candidates: Array<(m: T) => string | undefined>,
): string[] | undefined {
  const separates = (marks: Array<string | undefined>): marks is string[] =>
    !marks.some((x) => !x) && new Set(marks).size === marks.length

  for (const pick of candidates) {
    const marks = group.map(pick)
    if (separates(marks)) return marks
  }
  // Widen the two address-shaped discriminators until they do separate. 40 is
  // a full address; nothing longer exists to try.
  for (const width of [6, 8, 12, 40]) {
    for (const source of [
      (m: T) => m.refs?.oracle,
      (m: T) => m.venue.split('_').pop(),
      (m: T) => m.ref,
    ]) {
      const marks = group.map((m) => abbreviate(source(m), width))
      if (separates(marks)) return marks
    }
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Exitability
// ---------------------------------------------------------------------------

/**
 * Exit modes that CLAIM a same-block exit. Only these can be "illiquid".
 *
 * `market-sale` belongs here even though it is not a redemption: selling a
 * Pendle PT into its AMM settles in the same block, and the question this flag
 * asks is whether the money can leave NOW. On a market-sale venue `liquidity`
 * IS the pool depth, so zero depth means the position cannot be exited at any
 * price — the same condition, reached by a different mechanism.
 */
const SAME_BLOCK_EXIT_MODES: ReadonlySet<string> = new Set([
  'instant',
  'instant-capped',
  'market-sale',
])

/**
 * Can the money actually leave?
 *
 * A venue advertising a same-block exit while reporting ZERO liquidity against
 * a non-zero balance is enterable but not exitable — the Clearstar shape: $1.8M
 * of TVL, an "Instant" exit, and nothing to withdraw.
 *
 * Two conditions are load-bearing:
 *
 *  - **Only same-block modes qualify.** A cooldown or request-based vault
 *    legitimately reports no instant liquidity; that is its design, not a
 *    defect, and flagging it would condemn every well-behaved queued vault.
 *  - **`undefined` is not zero.** Several providers do not report liquidity at
 *    all. Treating "not published" as "none" would condemn them on a field they
 *    never sent, so an unknown liquidity is never flagged.
 *
 * Mirrored by the `illiquid` column in the `v_earn_latest` migration; a test in
 * yield-tracer pins the two against each other.
 */
export function isIlliquid(input: {
  exitMode?: string
  tvlUsd?: number
  liquidityUsd?: number
}): boolean {
  if (!input.exitMode || !SAME_BLOCK_EXIT_MODES.has(input.exitMode))
    return false
  if (!input.tvlUsd || input.tvlUsd <= 0) return false
  if (input.liquidityUsd === undefined) return false
  return input.liquidityUsd <= 0
}

/**
 * Exit modes whose route is UNCAPPED: the buffer `liquidity` reports says
 * nothing about how much can ultimately leave, only how much can leave THIS
 * BLOCK. A queued LST (Lido), a cooldown vault (sUSDe) and a request-based
 * vault (Lagoon) all publish `liquidity: 0` by design — see the per-mode
 * contract in `savings/types.ts` — while their exits work at any size.
 *
 * `fee-or-queued` belongs here even though it reports a positive buffer: that
 * number is the INSTANT leg only, and the queued leg behind it is explicitly
 * not inventory-capped (savings README, exit-fee table). `at-maturity` is
 * uncapped too — the money returns on a date, all of it.
 *
 * The set deliberately mixes mechanically-enforced routes (queue, cooldown)
 * with operator-processed ones (request-based, off-chain): whether to TRUST an
 * operator is a counterparty question the term sheet answers, not a liquidity
 * question a USD floor can.
 */
const UNCAPPED_EXIT_MODES: ReadonlySet<string> = new Set([
  'queued',
  'fixed-cooldown',
  'fee-or-queued',
  'instant-or-queued',
  'request-based',
  'off-chain',
  'at-maturity',
])

/**
 * Can at least `minUsd` actually leave this row, on ANY route?
 *
 * The predicate behind `?minLiquidityUsd=`. Its scalar predecessor
 * (`liquidity.usd >= X`) was 99 % false positive by dollar weight: it dropped
 * $31.4B of chain-1 TVL of which $31.4B had a working uncapped exit — every
 * large LST and cooldown vault — while the genuinely stuck rows it exists to
 * catch totaled ~$300M. Measured by `test/earn/liquidityFilterAudit.ts`,
 * which pins this predicate against the live catalogue.
 *
 * Three rules:
 *
 *  - **A closed exit fails at any size.** `canWithdraw: false` means no route
 *    is open, whatever the mode says.
 *  - **An uncapped route passes at any size.** The buffer is a latency fact
 *    on these rows, not a capacity fact.
 *  - **On capped routes the buffer IS the capacity** — same-block redemption
 *    (`instant`, `instant-capped`) and market exits (`market-sale`,
 *    `dex-only`, where `liquidity` is book depth) compare it against the
 *    floor. Unreported liquidity is kept, not dropped: the TVL floor's
 *    "unknown is not worthless" rule, which `isIlliquid` already follows.
 */
export function meetsLiquidityFloor(
  input: {
    exitMode?: string
    canWithdraw?: boolean
    liquidityUsd?: number
  },
  minUsd: number,
): boolean {
  if (input.canWithdraw === false) return false
  if (input.exitMode && UNCAPPED_EXIT_MODES.has(input.exitMode)) return true
  if (input.liquidityUsd === undefined) return true
  return input.liquidityUsd >= minUsd
}

/**
 * Lender families whose PROTOCOL name differs from their display brand.
 *
 * `brand` is the versioned label a row renders (`Silo V2`, `Aave V3`);
 * `protocol.name` is the axis the facet groups on, and it must be the protocol
 * itself or one protocol lands in several buckets. It did — six of them:
 *
 * ```
 * Morpho Blue(2055) + Morpho(87)        Euler V2(530) + Euler(46)
 * Silo V2(170) + Silo V3(74) + Silo(4)  Gearbox V3(167) + Gearbox(3)
 * Aave V3(78) + Aave V4(69) + Aave(5)   Compound V3(73) + Compound V2(19)
 * ```
 *
 * The vault half already says "Euler"/"Morpho"/"Silo", so a user filtering by
 * protocol "Euler" got the 46 Earn vaults and none of the 530 markets — the
 * markets were under a bucket they had no reason to look in.
 *
 * Morpho MIDNIGHT is deliberately absent: it is a fixed-rate order book, not a
 * Blue deployment, and merging it would group two unrelated products.
 */
const PROTOCOL_ALIASES: Record<string, string> = {
  MORPHO_BLUE: 'Morpho',
  EULER_V2: 'Euler',
  SILO_V2: 'Silo',
  SILO_V3: 'Silo',
  GEARBOX_V3: 'Gearbox',
  AAVE_V2: 'Aave',
  AAVE_V3: 'Aave',
  AAVE_V4: 'Aave',
  COMPOUND_V2: 'Compound',
  COMPOUND_V3: 'Compound',
}

/**
 * Providers that are a CATEGORY, not a protocol.
 *
 * `vault.savings` covers Sky, Ethena, Maple, Apyx…; `vault.lst` covers Lido,
 * Binance, EtherFi. For these the row's brand names the actual protocol, and
 * there is no separate curator — Ethena is not a "curator of Savings", it IS
 * the protocol. Reporting it the other way round inverts the two fields that
 * exist precisely to be told apart.
 *
 * Contrast the curated providers (morpho, euler-earn, lagoon, lista, gearbox),
 * where the protocol is fixed and the brand genuinely names a third party
 * running one instance of it.
 */
const CATEGORY_PROVIDERS: ReadonlySet<string> = new Set(['savings', 'lst'])

export interface EarnProtocolAndCurator {
  protocol: { key: string; name: string }
  curator?: { name?: string; entity?: string }
}

/**
 * Split a row's identity into the protocol it IS and the curator that runs it.
 *
 * **One resolver for both halves of the listing.** The lending half used to
 * assign `protocol` inline, which meant two definitions of the same idea that
 * could drift — and did: the lending side set `protocol.key` to the PER-MARKET
 * venue while the vault side set the stable `vault.<provider>`, so the one
 * field a client would cache on meant different things depending on the row.
 *
 * Four shapes, all real in the data:
 *
 *  - **curated vault** (Morpho, Euler, Lagoon, Lista, Gearbox) — protocol
 *    fixed by the provider, brand is a third party: `Morpho` +
 *    `Steakhouse Financial`.
 *  - **category vault** (savings, lst) — the brand IS the protocol: `Ethena`,
 *    `Lido`, with no curator. `vault.savings` spans Sky, Ethena and Maple;
 *    reporting Ethena as a "curator of Savings" inverts the two fields that
 *    exist precisely to be told apart.
 *  - **self-branded vault** (Fluid, Silo, Pendle, GMX, and every uncurated
 *    provider) — brand equals the protocol, so a curator would just repeat it.
 *  - **lending market** — the protocol is the lender family. No lender
 *    publishes a curator today; the parameter is still honoured so that when
 *    one does (a curated Morpho Blue market list, say) it needs no new branch.
 */
export function resolveEarnIdentity(
  venue: string,
  brand: string | undefined,
  /**
   * The protocol as PUBLISHED, from `lender-labels.json`'s `protocols` map by
   * way of `lenderInfo.protocol`.
   *
   * Authoritative when present. `PROTOCOL_ALIASES` below is the fallback for
   * the window before that map reaches the public-data fetchers — deriving a
   * name in this module and correcting it downstream is the shape this whole
   * section exists to end.
   */
  publishedProtocol?: string,
): EarnProtocolAndCurator {
  const isVault = venue.startsWith(VAULT_VENUE_PREFIX)
  const provider = isVault ? venue.slice(VAULT_VENUE_PREFIX.length) : venue
  const providerBrand = venueBrand(venue)
  // Stable across every market of one lender, unlike `venue`. `?protocol=`
  // filters by NAME (so both halves of a protocol can share a bucket); this is
  // what a client keys a cache or a route on.
  const key = venueBrandKey(venue)
  // The PROTOCOL, which is not always the brand: `Silo V2` and `Silo V3` are
  // both Silo, and the vault half already calls it that.
  const protocolName =
    publishedProtocol?.trim() || PROTOCOL_ALIASES[key] || providerBrand

  // A category provider's brand names the protocol, so several protocols
  // legitimately share one key while the names discriminate.
  if (isVault && CATEGORY_PROVIDERS.has(provider)) {
    return { protocol: { key, name: brand?.trim() || protocolName } }
  }

  const name = brand?.trim()
  // Equal ⇒ self-branded, not curated. Repeating it would imply a third party
  // that does not exist.
  if (!name || name.toLowerCase() === providerBrand.toLowerCase()) {
    return { protocol: { key, name: protocolName } }
  }

  return { protocol: { key, name: protocolName }, curator: { name } }
}
